import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";

// ─── Inline icons (kept local so this page has no extra deps) ────────────────
function PlusIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function TrashIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>; }
function FilmIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="7" y1="3" x2="7" y2="21"/><line x1="17" y1="3" x2="17" y2="21"/><line x1="2" y1="8" x2="7" y2="8"/><line x1="2" y1="16" x2="7" y2="16"/><line x1="17" y1="8" x2="22" y2="8"/><line x1="17" y1="16" x2="22" y2="16"/></svg>; }

export default function HomeVideo({ title }) {
  const [reels, setReels]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showForm, setShowForm] = useState(false);

  const [caption, setCaption]     = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const loadReels = () => {
    setLoading(true);
    setLoadError("");
    apiFetch("/home/reels.php", { method: "GET", auth: false })
      .then((res) => setReels(res.data || []))
      .catch((err) => setLoadError(err.message || "Could not load reels."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadReels(); }, []);

  const resetForm = () => {
    setCaption("");
    setVideoFile(null);
    setThumbFile(null);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!caption.trim())  return setFormError("Please enter a name/caption for this video.");
    if (!videoFile)       return setFormError("Please choose a video file to upload.");

    const formData = new FormData();
    formData.append("caption", caption.trim());
    formData.append("video", videoFile);
    if (thumbFile) formData.append("thumbnail", thumbFile);

    setSubmitting(true);
    try {
      await apiFetch("/home/reels.php", { method: "POST", body: formData });
      resetForm();
      setShowForm(false);
      loadReels();
    } catch (err) {
      setFormError(err.message || "Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this reel? This cannot be undone.")) return;
    try {
      await apiFetch(`/home/reels.php?id=${id}`, { method: "DELETE" });
      setReels((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      alert(err.message || "Could not delete this reel.");
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[20px] sm:text-[26px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>
            {title || "Home Reels"}
          </h1>
          <p className="text-[12px] sm:text-sm mt-0.5" style={{ color: T.textSec }}>
            Manage the videos shown in the "As seen on Instagram" section of the homepage.
          </p>
        </div>
        <button
          onClick={() => { setShowForm((s) => !s); if (showForm) resetForm(); }}
          className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-xl text-[12px] sm:text-[13px] font-semibold text-white whitespace-nowrap flex-shrink-0"
          style={{ background: T.gold }}
        >
          {showForm ? "Cancel" : (<><PlusIcon /> Add Reel</>)}
        </button>
      </div>

      {/* Upload form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl bg-white p-4 sm:p-5 max-w-xl"
          style={{ border: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
        >
          <p className="text-[14px] sm:text-[15px] font-bold mb-4" style={{ color: T.textPri, fontFamily: T.serif }}>
            Upload a new reel
          </p>

          <div className="mb-4">
            <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.textPri }}>
              Video name / caption
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Unboxing our new 2026 Planner"
              className="w-full px-3 py-2.5 rounded-xl text-[13px] focus:outline-none focus:ring-2"
              style={{ border: `1px solid ${T.border}`, boxShadow: "none" }}
            />
          </div>

          <div className="mb-4">
            <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.textPri }}>
              Video file (mp4, webm, mov)
            </label>
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              className="text-[12.5px] w-full file:mr-3 file:py-2 file:px-3.5 file:rounded-lg file:border-0 file:text-[12px] file:font-semibold file:cursor-pointer"
              style={{ color: T.textSec }}
            />
          </div>

          <div className="mb-5">
            <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.textPri }}>
              Thumbnail image (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setThumbFile(e.target.files?.[0] || null)}
              className="text-[12.5px] w-full file:mr-3 file:py-2 file:px-3.5 file:rounded-lg file:border-0 file:text-[12px] file:font-semibold file:cursor-pointer"
              style={{ color: T.textSec }}
            />
          </div>

          {formError && (
            <p className="text-[12.5px] font-semibold mb-4" style={{ color: T.danger }}>{formError}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white"
            style={{ background: submitting ? T.goldL : T.gold, color: submitting ? T.goldD : "#fff", cursor: submitting ? "default" : "pointer" }}
          >
            {submitting ? "Uploading..." : "Upload Reel"}
          </button>
        </form>
      )}

      {/* Reels list */}
      <div className="rounded-2xl bg-white overflow-hidden" style={{ border: `1px solid ${T.border}` }}>
        <div className="flex items-center justify-between px-4 sm:px-5 py-4" style={{ borderBottom: `1px solid ${T.border}` }}>
          <div>
            <p className="text-[14px] sm:text-[15px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Uploaded Reels</p>
            <p className="text-[10px] sm:text-[11px]" style={{ color: T.textSec }}>{reels.length} video{reels.length === 1 ? "" : "s"}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full animate-spin" style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
              <p className="text-[12px]" style={{ color: T.textSec }}>Loading reels...</p>
            </div>
          </div>
        ) : loadError ? (
          <p className="text-[13px] font-semibold text-center py-10" style={{ color: T.danger }}>{loadError}</p>
        ) : reels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center px-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: T.goldL, color: T.gold }}>
              <FilmIcon />
            </div>
            <p className="text-[13px] font-semibold" style={{ color: T.textPri }}>No reels uploaded yet</p>
            <p className="text-[12px] mt-1" style={{ color: T.textSec }}>Click "+ Add Reel" above to add your first one.</p>
          </div>
        ) : (
          <div className="p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {reels.map((reel) => (
              <div
                key={reel.id}
                className="rounded-2xl overflow-hidden hover:shadow-md transition-all"
                style={{ border: `1px solid ${T.border}` }}
              >
                <div className="relative w-full bg-black" style={{ aspectRatio: "9 / 16" }}>
                  <video
                    src={reel.video_url}
                    poster={reel.thumbnail_url}
                    controls
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-2.5">
                  <p className="text-[11.5px] leading-snug mb-2 line-clamp-2" style={{ color: T.textPri }}>
                    {reel.caption}
                  </p>
                  <button
                    onClick={() => handleDelete(reel.id)}
                    className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1.5 rounded-lg transition-colors"
                    style={{ border: `1px solid #F0D6D2`, color: T.danger }}
                  >
                    <TrashIcon /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}